import { Fragment } from "react";
import Link from "next/link";
import { useTranslation } from "next-i18next";
import useCountdown from "@/hooks/useCountdown";

const twoDigits = (value) => String(value).padStart(2, "0");

// Poppins has no tabular figures (tabular-nums changes nothing), so each digit gets the width
// of the widest one ("4", 0.677em). Otherwise the boxes resize every second as digits change.
const DIGIT_CLASS = "inline-block w-[0.68em] text-center";

const NUMBER_CLASS =
  "font-poppins text-[15.542px] leading-[12.628px] min-[992px]:text-[28.342px] min-[992px]:leading-[23.027px]";

export default function BannerCountdown({ text, endsAt, colors, href, buttonLabel }) {
  const { t } = useTranslation("common");
  const { days, hours, minutes, seconds } = useCountdown(endsAt);
  const units = [
    { key: "days", value: days, label: t("headerBannerDay") },
    { key: "hours", value: hours, label: t("headerBannerHour") },
    { key: "minutes", value: minutes, label: t("headerBannerMinute") },
    { key: "seconds", value: seconds, label: t("headerBannerSecond") },
  ];

  // Mobile: text at the start, countdown at the end. Desktop: equal side columns keep the
  // countdown centred whatever the text or button length; long text truncates in its column.
  return (
    <div className="relative flex w-full min-w-0 items-center justify-between gap-2 pe-[28px] ps-[23px] min-[992px]:grid min-[992px]:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] min-[992px]:gap-x-6 min-[992px]:px-[72px]">
      {text && (
        // Below ~400 px the design's text and countdown do not fit on one line: wrap to two lines
        // (2 × 21.4 px still fits the 48 px strip) instead of cutting a short message.
        <p className="min-w-0 text-xs font-semibold leading-[21.387px] ltr:font-poppins max-[991px]:line-clamp-2 min-[992px]:col-start-1 min-[992px]:truncate min-[992px]:text-base min-[992px]:leading-[26px]">
          {text}
        </p>
      )}
      {/* role=timer is not live by default, so screen readers are not told every second. */}
      <div role="timer" className="flex shrink-0 items-center gap-[4.387px] min-[992px]:col-start-2 min-[992px]:gap-2">
        {units.map(({ key, value, label }, index) => (
          <Fragment key={key}>
            {index > 0 && (
              <span aria-hidden="true" className={`${NUMBER_CLASS} font-medium`}>
                :
              </span>
            )}
            <span className="flex items-center gap-[2.194px] min-[992px]:gap-1">
              {/* dir=ltr: the digits are separate boxes now, and in RTL they would line up reversed ("03" → "30"). */}
              <span
                dir="ltr"
                className={`${NUMBER_CLASS} p-[4.387px] font-bold min-[992px]:p-2`}
                style={{ backgroundColor: colors.digitBox }}
              >
                {[...twoDigits(value)].map((digit, digitIndex) => (
                  <span key={digitIndex} className={DIGIT_CLASS}>
                    {digit}
                  </span>
                ))}
              </span>
              <span className="text-[8.419px] leading-[12.628px] ltr:font-poppins min-[992px]:text-[15.352px] min-[992px]:leading-[23.027px]">
                {label}
              </span>
            </span>
          </Fragment>
        ))}
      </div>
      {href && buttonLabel && (
        <Link
          href={href}
          className="relative z-20 hidden h-9 shrink-0 items-center rounded-lg px-2.5 text-base font-bold leading-[16.9px] ltr:font-poppins ltr:font-medium min-[992px]:col-start-3 min-[992px]:flex min-[992px]:justify-self-end"
          style={{ backgroundColor: colors.button, color: colors.buttonText }}
        >
          {buttonLabel}
        </Link>
      )}
    </div>
  );
}
